import { gql } from 'graphql-request';

import type { RequestDocument } from 'graphql-request';

// ── AI Model Provider & Model Registry ──

export const AI_MODEL_PROVIDER_QUERY_FRAGMENT = gql`
  {
    id
    displayName
    internalName
    endpointUrl
    isActive
    notes
  }
`;

export const ALL_AI_MODEL_PROVIDERS_QUERY: RequestDocument = gql`
  query {
    allAiModelProviders(first: 100, filter: { isActive: { eq: true } }) ${AI_MODEL_PROVIDER_QUERY_FRAGMENT}
  }
`;

export const AI_MODEL_QUERY_FRAGMENT = gql`
  {
    id
    internalName
    displayName
    providerLink ${AI_MODEL_PROVIDER_QUERY_FRAGMENT}
    modelId
    capabilities
    isDefault
    isActive
    maxTokens
    notes
  }
`;

export const ALL_AI_MODELS_QUERY: RequestDocument = gql`
  query {
    allAiModels(first: 100, filter: { isActive: { eq: true } }) ${AI_MODEL_QUERY_FRAGMENT}
  }
`;

export const AI_MODEL_BY_ID_QUERY: RequestDocument = gql`
  query getAiModel($id: ItemId!) {
    aiModel(filter: { id: { eq: $id } }) ${AI_MODEL_QUERY_FRAGMENT}
  }
`;

// ── AI Flows ──

export const AI_FLOW_QUERY_FRAGMENT = gql`
  {
    id
    title
    internalTitle
    category
    steps {
      label
      systemMessageTemplate
      userPromptTemplate
      overrideMessagesFromStep
      maxAttempts
      iterateOver
      modelName
      temperature
      temperatureFunction
      retryConditionFunction
      filterFunction
      reducerFunction
      resultType
    }
    resultMapperFunction
    logEnabled
    userPromptDescription
  }
`;

// Extended fragment that includes the optional model link fields (PLAT-9029).
// Use this in flow queries after the migration has run.
export const AI_FLOW_WITH_MODELS_QUERY_FRAGMENT = gql`
  {
    id
    title
    internalTitle
    category
    steps {
      label
      systemMessageTemplate
      userPromptTemplate
      overrideMessagesFromStep
      maxAttempts
      iterateOver
      modelName
      model ${AI_MODEL_QUERY_FRAGMENT}
      fallbackModels ${AI_MODEL_QUERY_FRAGMENT}
      temperature
      temperatureFunction
      retryConditionFunction
      filterFunction
      reducerFunction
      resultType
    }
    resultMapperFunction
    logEnabled
  }
`;

export const AI_FLOW_BY_TITLE_AND_ACTIVE_QUERY: RequestDocument = gql`
  query getAiFlow($title: String!) {
    aiFlow(
      filter: {
        title: { eq: $title }
        active: { eq: true }
      }
    ) ${AI_FLOW_WITH_MODELS_QUERY_FRAGMENT}
  }
`;

// Base query without model link fields — used as fallback before migration runs
export const AI_FLOW_BY_TITLE_AND_ACTIVE_BASE_QUERY: RequestDocument = gql`
  query getAiFlow($title: String!) {
    aiFlow(
      filter: {
        title: { eq: $title }
        active: { eq: true }
      }
    ) ${AI_FLOW_QUERY_FRAGMENT}
  }
`;

export const AI_PROMPT_QUERY_FRAGMENT = gql`
  {
    id
    promptFields {
      id
      name
      description
    }
    description
    title
    followUp
    targetWordCount
    targetCharacterCount
    userPromptDescription
  }
`;

// Extended fragment that includes the optional model link (PLAT-9028) and the
// optional distribution channel link (PLAT-11038).
// Use this only in standalone prompt queries — NOT in the story/customer-type
// query (misc-gql.ts) which would break if the migration hasn't run yet.
export const AI_PROMPT_WITH_MODEL_QUERY_FRAGMENT = gql`
  {
    id
    promptFields {
      id
      name
      description
    }
    description
    title
    internalTitle
    followUp
    promptType
    targetWordCount
    targetCharacterCount
    model ${AI_MODEL_QUERY_FRAGMENT}
    distributionChannel {
      slug
    }
  }
`;

export const ALL_AI_PROMPTS_QUERY: RequestDocument = gql`
  query {
    allAiPrompts(first: 100) ${AI_PROMPT_WITH_MODEL_QUERY_FRAGMENT}
  }
`;

/**
 * Prompt -> distribution channel association only (PLAT-11038).
 *
 * Deliberately narrow and separate from ALL_AI_PROMPTS_QUERY: that query is
 * capped at `first: 100` while the prompt set is already larger (135 in dev), so
 * reusing it would silently drop later prompts' channels and fall the preview
 * back to LinkedIn. 500 is DatoCMS's per-page maximum; the repository warns if a
 * full page comes back so the cap surfaces instead of truncating quietly.
 * Fetching just id + slug also avoids pulling every prompt's system-prompt text.
 */
export const AI_PROMPT_CHANNELS_QUERY: RequestDocument = gql`
  query {
    allAiPrompts(first: 500) {
      id
      distributionChannel {
        slug
      }
    }
  }
`;

/** Page size of AI_PROMPT_CHANNELS_QUERY — DatoCMS's `first` maximum. */
export const AI_PROMPT_CHANNELS_PAGE_SIZE = 500;

export const AI_PROMPT_BY_TITLE_QUERY: RequestDocument = gql`
  query getAiPrompt($title: String!) {
    aiPrompt(
      filter: { title: { eq: $title } }
    ) ${AI_PROMPT_WITH_MODEL_QUERY_FRAGMENT}
  }
`;

export const AI_PROMPT_BY_ID_QUERY: RequestDocument = gql`
  query getAiPrompt($id: ItemId!) {
    aiPrompt(
      filter: { id: { eq: $id } }
    ) ${AI_PROMPT_WITH_MODEL_QUERY_FRAGMENT}
  }
`;

// Base queries without model field — used as fallback before migration runs
export const ALL_AI_PROMPTS_BASE_QUERY: RequestDocument = gql`
  query {
    allAiPrompts(first: 100) ${AI_PROMPT_QUERY_FRAGMENT}
  }
`;

export const AI_PROMPT_BY_TITLE_BASE_QUERY: RequestDocument = gql`
  query getAiPrompt($title: String!) {
    aiPrompt(
      filter: { title: { eq: $title } }
    ) ${AI_PROMPT_QUERY_FRAGMENT}
  }
`;

export const AI_PROMPT_BY_ID_BASE_QUERY: RequestDocument = gql`
  query getAiPrompt($id: ItemId!) {
    aiPrompt(
      filter: { id: { eq: $id } }
    ) ${AI_PROMPT_QUERY_FRAGMENT}
  }
`;

export const STORIES_ARTIFACT_AI_TITLE_QUERY: RequestDocument = gql`
  query getStories($ids: [ItemId]) {
    allStories(filter: { id: { in: $ids } }) {
      id
      storyArtifacts {
        id
        title
      }
    }
  }
`;

export const SHOWCASES_ARTIFACT_AI_TITLE_QUERY: RequestDocument = gql`
  query getShowcases($ids: [ItemId]) {
    allShowcases(filter: { id: { in: $ids } }) {
      id
      organizationArtifacts {
        id
        title
      }
    }
  }
`;
